import notification from "antd/es/notification";
import { camelCase, uniq, uniqBy } from "lodash-es";
import { defineMessages } from "react-intl";
import { all, call, put, select, takeLatest } from "redux-saga/effects";

import CountryCode from "@mapmycustomers/shared/enum/CountryCode";
import EntityType from "@mapmycustomers/shared/enum/EntityType";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import Address from "@mapmycustomers/shared/types/Address";
import Cadenced from "@mapmycustomers/shared/types/base/Cadenced";
import { GeoManagementState } from "@mapmycustomers/shared/types/base/Located";
import CustomFieldValue from "@mapmycustomers/shared/types/customField/CustomFieldValue";
import CustomFieldValuesUpsertResponse from "@mapmycustomers/shared/types/customField/CustomFieldValuesUpsertResponse";
import {
  AnyEntityId,
  Company,
  Deal,
  EntitiesSupportingBulkEdit,
  EntitiesSupportingCustomFieldsBulkEdit,
  EntitiesSupportingRoutes,
  EntityTypesSupportingBulkEdit,
  EntityTypesSupportingCustomFieldsBulkEdit,
  EntityTypesSupportingRoutes,
  Person,
} from "@mapmycustomers/shared/types/entity";
import Route, {
  CreateRouteStopItem,
  PreviewRoutePayload,
  RoutePreview,
  RoutePreviewMetrics,
  RouteStop,
  RouteStopBulkItem,
} from "@mapmycustomers/shared/types/entity/Route";
import UpdatePersonPayload from "@mapmycustomers/shared/types/entity/UpdatePersonPayload";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import i18nService from "@app/config/I18nService";
import DealLockReason from "@app/enum/DealLockReason";
import { BulkDeleteResponse, BulkUpdateResponse } from "@app/store/api/ApiService";
import { callApi } from "@app/store/api/callApi";
import parseBulkDeleteOutcome from "@app/store/api/utils/parseBulkDeleteOutcome";
import { getDealLockGetter } from "@app/store/deal";
import { fetchAllFunnels } from "@app/store/deal/actions";
import { handleError } from "@app/store/errors/actions";
import { reloadGroups } from "@app/store/groups/actions";
import { getOrganizationId } from "@app/store/iam";
import { routeValidationErrorMessages } from "@app/store/routes/messages";
import { notifyAboutChanges } from "@app/store/uiSync/actions";
import { MAX_ROUTE_STOPS } from "@app/util/consts";
import formatCountryName from "@app/util/countries/formatCountryName";
import dealLockMessages from "@app/util/deal/dealLockMessages";
import { canApplyDealEdit } from "@app/util/deal/getDealLock";
import { hasDealLockCode } from "@app/util/deal/isDealLockRefusal";
import getErrorMessage from "@app/util/errorHandling/getErrorMessage";
import getErrorMessageByCode from "@app/util/errorHandling/platformErrorCodes";
import { DealFieldName } from "@app/util/fieldModel/DealFieldModel";
import { isCustomField } from "@app/util/fieldModel/impl/assert";
import { PersonFieldName } from "@app/util/fieldModel/PersonFieldModel";
import getLayoutModelByEntityType from "@app/util/layout/impl";
import buildPreviewPayloadFromRoute from "@app/util/route/buildPreviewPayloadFromRoute";
import isRouteComplete from "@app/util/route/isRouteComplete";
import getRoutePreviewMetrics from "@app/util/route/routePreviewMetrics";
import { getEntityTypeDisplayName } from "@app/util/ui";

import {
  bulkAddEntitiesToGroups,
  bulkAddEntitiesToRoutes,
  bulkRemoveEntitiesFromGroups,
  bulkRemoveEntitiesFromRoutes,
  deleteEntities,
  filterRoutes,
  initializeRouteModal,
  updateEntities,
} from "./actions";
import getRowErrorsDescription from "./getRowErrorsDescription";

const messages = defineMessages({
  deleteFailed: {
    id: "selectBar.delete.error",
    defaultMessage:
      "{count} {count, plural, one {{entityType, select, accounts {Company was} contacts {Person was} crmActivities {Activity was} deals {Deal was} trips {Trip was} other {Record was}}} other {{entityType, select, accounts {Companies were} contacts {People were} crmActivities {Activities were} deals {Deals were} trips {Trips were} other {Records were}}}} not deleted",
    description: "None of the selected entities could be deleted from the Select Bar",
  },
  deleteWarning: {
    id: "selectBar.delete.warning",
    defaultMessage:
      "Only {count} {count, plural, one {{entityType, select, accounts {Company} contacts {Person} crmActivities {Activity} deals {Deal} trips {Trip} other {Record}}} other {{entityType, select, accounts {Companies} contacts {People} crmActivities {Activities} deals {Deals} trips {Trips} other {Records}}}} deleted successfully",
    description: "Entities partially deleted successfully from the Select Bar",
  },
  deleteWarningDescription: {
    id: "selectBar.delete.warning.description",
    defaultMessage:
      "{failedCount, plural, one {# record} other {# records}} could not be deleted. {failedCount, plural, one {It may have} other {They may have}} already been deleted, or you may not have permission to delete {failedCount, plural, one {it} other {them}}.",
    description: "Entities partially deleted successfully from the Select Bar description",
  },
  failedTitle: {
    id: "selectBar.routes.add.error.title",
    defaultMessage: "Unable to add to “{routeName}”",
    description: "Title of a notification to show when failed to bulk add to route",
  },
  failedToBuild: {
    id: "selectBar.routes.add.error.cantBuild",
    defaultMessage:
      "We were unable to construct a route across the given terrain. Please double check that all intended stops can be navigated to.",
    description:
      "An error message to appear when trying to bulk add entities to route, but resulting route is not possible to build",
  },
  failedTooManyStops: {
    id: "selectBar.routes.add.error.tooManyStops",
    defaultMessage:
      "There are too many existing stops to build the route. Please remove stops before attempting to add new ones.",
    description:
      "An error message to appear when trying to bulk add entities to route, but there will be too many stops after that",
  },
  noRecordsToAdd: {
    id: "selectBar.routes.add.noRecordsToAdd",
    defaultMessage: "No new records to add to the selected route(s)",
    description: "Info notification when the add-to-routes operation had nothing new to add",
  },
  noRecordsToRemove: {
    id: "selectBar.routes.delete.noRecordsToRemove",
    defaultMessage: "No records to remove from the selected route(s)",
    description: "Info notification when the remove-from-routes operation had nothing to remove",
  },
  recordEntityName: {
    id: "selectBar.routes.recordEntityName",
    defaultMessage: "{count, plural, one {record} other {records}}",
    description: "Record label used in the route add/remove success notifications",
  },
  recordsSuccessfullyDeleted: {
    id: "selectBar.records.delete.success.title",
    defaultMessage: "{plural, select, true {Records} other {Record}} successfully deleted",
    description: "Entities are deleted successfully from the Select Bar",
  },
  successfullyAddedToGroups: {
    id: "selectBar.groups.add.success",
    defaultMessage: "{count} {entityName} added to group(s)",
    description: "Entities are added successfully into the groups using select bar",
  },
  successfullyAddedToRoutes: {
    id: "selectBar.routes.add.success",
    defaultMessage:
      "{count} {entityName} added to{routeCount, plural, one {} other { {routeCount}}} {routeCount, plural, one {route} other {routes}}",
    description: "Entities are added successfully into the routes using select bar",
  },
  successfullyDeleted: {
    id: "selectBar.delete.success.title",
    defaultMessage: "{entityName} successfully deleted",
    description: "Entities are deleted successfully from the Select Bar",
  },
  successfullyDeletedDescription: {
    id: "selectBar.delete.success.description",
    defaultMessage:
      "To restore {plural, select, true {these records} other {this record}}, go to Data in Settings.",
    description: "Entities are deleted successfully description from the Select Bar",
  },
  successfullyDeletedFromGroups: {
    id: "selectBar.groups.delete.success",
    defaultMessage: "{count} {entityName} deleted from group(s)",
    description: "Entities are deleted successfully from the groups using select bar",
  },
  successfullyDeletedFromRoutes: {
    id: "selectBar.routes.delete.success",
    defaultMessage:
      "{count} {entityName} deleted from {routeCount, plural, one {route} other {routes}}",
    description: "Entities are deleted successfully from the routes using select bar",
  },
  successfullyUpdated: {
    id: "selectBar.update.success",
    defaultMessage:
      "{count} {count, plural, one {{entityType, select, accounts {Company} contacts {Person} crmActivities {Activity} deals {Deal} trips {Trip} other {Record}}} other {{entityType, select, accounts {Companies} contacts {People} crmActivities {Activities} deals {Deals} trips {Trips} other {Records}}}} updated",
    description: "Entities are updated successfully from the Bulk Edit Modal",
  },
  updateFailed: {
    id: "selectBar.update.error",
    defaultMessage:
      "{count} {count, plural, one {{entityType, select, accounts {Company was} contacts {Person was} crmActivities {Activity was} deals {Deal was} trips {Trip was} other {Record was}}} other {{entityType, select, accounts {Companies were} contacts {People were} crmActivities {Activities were} deals {Deals were} trips {Trips were} other {Records were}}}} not updated",
    description: "Entities are not updated successfully from the Bulk Edit Modal",
  },
  updateWarning: {
    id: "selectBar.update.warning",
    defaultMessage:
      "Only {count} {count, plural, one {{entityType, select, accounts {Company} contacts {Person} crmActivities {Activity} deals {Deal} trips {Trip} other {Record}}} other {{entityType, select, accounts {Companies} contacts {People} crmActivities {Activities} deals {Deals} trips {Trips} other {Records}}}} updated successfully",
    description: "Entities partially updated successfully from the Bulk Edit Modal",
  },
  updateWarningDescription: {
    id: "selectBar.update.warning.description",
    defaultMessage:
      "There was an issue updating this value for {total, plural, one {a record} other {all {total} records}} selected. Please try again. If this issue persists, please contact support.",
    description: "Entities partially updated successfully from the Bulk Edit Modal description",
  },
});

// A lock set after the funnels were loaded: reload them, so every picker and note shows it
function* reloadFunnelsOnLockRefusal(
  rowErrors: Array<ReadonlyArray<{ code?: string }> | undefined>
) {
  if (rowErrors.some(hasDealLockCode)) {
    yield put(fetchAllFunnels.request());
  }
}

export function* onDeleteEntities({
  payload: { entityIds, entityType, multiEntities, onFailure, onSuccess },
}: ReturnType<typeof deleteEntities.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const response: BulkDeleteResponse = yield callApi(
      "deleteEntities",
      orgId,
      entityType,
      entityIds
    );

    const { deletedIds, failedCount, failedRecords } = parseBulkDeleteOutcome(entityIds, response);
    yield* reloadFunnelsOnLockRefusal(failedRecords.map(({ errors }) => errors));

    const intl = i18nService.getIntl();
    if (intl && failedCount) {
      const isPartialSuccess = deletedIds.length > 0;
      const isDeal = entityType === EntityType.DEAL;
      // a deal row rejected by its lock carries a readable message of its own; platform can also
      // report a whole-batch validation error whose entries carry no `errors` list
      const errors = uniq(
        (isDeal ? failedRecords : failedRecords.slice(0, 1))
          .flatMap((row) => row.errors ?? [])
          .map((error) => getErrorMessageByCode(intl, error.code, error) ?? error.message)
      );

      (isPartialSuccess ? notification.warn : notification.error)({
        description: getRowErrorsDescription(
          errors,
          isDeal,
          isPartialSuccess,
          intl.formatMessage(messages.deleteWarningDescription, { failedCount })
        ),
        message: intl.formatMessage(
          isPartialSuccess ? messages.deleteWarning : messages.deleteFailed,
          {
            count: isPartialSuccess ? deletedIds.length : failedCount,
            entityType: camelCase(entityType),
          }
        ),
      });
    } else if (intl) {
      notification.success({
        description: intl.formatMessage(messages.successfullyDeletedDescription, {
          plural: entityIds.length > 1,
        }),
        message: intl.formatMessage(
          multiEntities ? messages.recordsSuccessfullyDeleted : messages.successfullyDeleted,
          {
            entityName: getEntityTypeDisplayName(intl, entityType, {
              lowercase: false,
              plural: entityIds.length > 1,
            }),
            plural: multiEntities,
          }
        ),
      });
    }

    if (deletedIds.length > 0) {
      yield put(notifyAboutChanges({ deletedIds, entityType }));
    }

    yield put(deleteEntities.success());
    onSuccess();
  } catch (error) {
    yield put(deleteEntities.failure());
    yield put(handleError({ error }));
    onFailure?.();
  }
}

// The selected deals an edit can't touch because of their funnel or stage lock, by id, with why.
function* getLockedDealReasons(
  entities: EntitiesSupportingBulkEdit[],
  entityType: EntityTypesSupportingBulkEdit,
  field: IField
): Generator<unknown, Map<AnyEntityId, DealLockReason>, ReturnType<typeof getDealLockGetter>> {
  if (entityType !== EntityType.DEAL) {
    return new Map();
  }
  const getDealLock = yield select(getDealLockGetter);
  const changesStage = field.hasAnyFeature(FieldFeature.FUNNEL_FIELD);
  return new Map(
    (entities as Deal[]).flatMap((deal): [AnyEntityId, DealLockReason][] => {
      const lock = getDealLock(deal);
      return lock.reason && !canApplyDealEdit(lock, changesStage) ? [[deal.id, lock.reason]] : [];
    })
  );
}

export function* onUpdateEntities({
  payload: { entities: selectedEntities, entityType, field, onSuccess, primaryEntityId, value },
}: ReturnType<typeof updateEntities.request>) {
  // since the bulk edit endpoints don't return full payloads back, we're simulating update to
  // pass it into notifyAboutChanges
  let updatedEntities: EntitiesSupportingBulkEdit[] = [];
  const failedEntityIds = new Set<AnyEntityId>();
  let entitiesUpdatedCount = 0,
    entitiesFailedCount = 0;
  let errors: string[] = [];
  const isDeal = entityType === EntityType.DEAL;

  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const intl = i18nService.getIntl();

    const lockedDealReasons = yield* getLockedDealReasons(selectedEntities, entityType, field);
    Array.from(lockedDealReasons.keys()).forEach((id) => failedEntityIds.add(id));
    const entities = selectedEntities.filter(({ id }) => !lockedDealReasons.has(id));

    // when every selected record is locked, nothing is sent
    if (entities.length && isCustomField(field)) {
      entityType = entityType as EntityTypesSupportingCustomFieldsBulkEdit;

      const layoutModel = getLayoutModelByEntityType(entityType);

      const response: CustomFieldValuesUpsertResponse[] = yield callApi(
        "bulkUpsertCustomFieldsValues",
        true,
        orgId,
        entityType,
        (entities as EntitiesSupportingCustomFieldsBulkEdit[]).map((entity) => ({
          id: entity.id,
          layoutId: layoutModel.getLayoutFor(entity).id,
        })),
        [value as CustomFieldValue]
      );

      const erroredResponse = response.filter(
        (row) => row.errorCF.some(({ error }) => error.length) || row.error.length
      );
      yield* reloadFunnelsOnLockRefusal(
        erroredResponse.flatMap((row) => [row.error, ...row.errorCF.map(({ error }) => error)])
      );

      erroredResponse.forEach((row) => {
        switch (entityType) {
          case EntityType.ACTIVITY:
            failedEntityIds.add(row.crmActivityId!);
            break;
          case EntityType.COMPANY:
            failedEntityIds.add(row.accountId!);
            break;
          case EntityType.DEAL:
            failedEntityIds.add(row.dealId!);
            break;
          case EntityType.PERSON:
            failedEntityIds.add(row.contactId!);
            break;
        }
      });

      updatedEntities = (entities as EntitiesSupportingCustomFieldsBulkEdit[]).map((entity) => ({
        ...entity,
        customFields: failedEntityIds.has(entity.id)
          ? entity.customFields
          : (entity.customFields ?? []).map((oldValue) =>
              oldValue.esKey === field.name
                ? { ...oldValue, value: (value as CustomFieldValue).value }
                : oldValue
            ),
      }));

      // removing failed records from updatedEntities
      updatedEntities = updatedEntities.filter(({ id }) => !failedEntityIds.has(id));

      if (intl) {
        errors = erroredResponse.map(({ error, errorCF }) => {
          return (
            getErrorMessageByCode(intl, error[0]?.code ?? errorCF[0].error[0].code) ??
            error[0]?.message ??
            errorCF[0].error[0].message
          );
        });
      }
    } else if (entities.length) {
      const response: BulkUpdateResponse = yield callApi(
        "updateEntities",
        orgId,
        entityType,
        entities.map((entity) => {
          let updatedEntity: Partial<EntitiesSupportingBulkEdit> = {
            id: entity.id,
          };

          if (entityType !== EntityType.TRIP) {
            const layoutModel = getLayoutModelByEntityType(entityType);
            // We know that the layoutModel we received has same EntityType as entities
            // Also, layoutId is required by platform
            // @ts-ignore
            updatedEntity.layoutId = layoutModel.getLayoutFor(entity).id;
          }

          // currently platform requires full payload for trip objects
          if (entityType === EntityType.TRIP) {
            updatedEntity = entity;
          }

          if (field.hasFeature(FieldFeature.BULK_ADDRESS)) {
            if (typeof value === "object" && value) {
              updatedEntity = {
                ...(updatedEntity as Company | Person),
                geoManagementState: GeoManagementState.AUTOMATIC_PRESERVE_ADDRESS,
                geoPoint: null,
                ...value,
              };

              updatedEntities = entities.map((entity) => ({
                ...entity,
                geoManagementState: GeoManagementState.AUTOMATIC_PRESERVE_ADDRESS,
                geoPoint: null,
                ...value,
              }));
            }
          }
          if (field.hasFeature(FieldFeature.FREQUENCY_INTERVAL)) {
            const frequencyIntervalValue: Cadenced =
              typeof value === "object" && value
                ? value
                : {
                    cadenceInterval: null,
                    crmActivityTypeId: null,
                  };

            updatedEntity = {
              ...updatedEntity,
              ...frequencyIntervalValue,
            };
            updatedEntities = entities.map((entity) => ({ ...entity, ...frequencyIntervalValue }));
          } else if (field.name === DealFieldName.FUNNEL) {
            if (typeof value === "object" && value) {
              updatedEntity = {
                ...updatedEntity,
                ...value,
              };
              updatedEntities = entities.map((entity) => ({ ...entity, ...value }));
            }
          } else {
            const updatedEntityRow = { [field.name]: value };

            // Ignoring because we're kinda sure that the field passed in this action is always
            // related to the given entityType. Hence, this expression is valid.
            // @ts-ignore
            updatedEntity[field.name] = value;
            if (
              field.hasFeature(FieldFeature.COUNTRY_FIELD) &&
              intl &&
              value &&
              entity.entity !== EntityType.TRIP
            ) {
              const countryName = formatCountryName(intl, value as CountryCode);
              updatedEntityRow.country = countryName;
              // since we're now processing the country field, we know that such field exists on the updatedEntity object
              // @ts-ignore
              updatedEntity.country = countryName;
              (updatedEntity as Company | Person).geoManagementState = GeoManagementState.MANUAL;
              (updatedEntity as Company | Person).geoAddress = {
                ...(entity as Company | Person).geoAddress,
                country: updatedEntityRow.country as Address["country"],
                countryCode: updatedEntityRow.countryCode as Address["countryCode"],
              };
            }
            if (field.name === PersonFieldName.COMPANIES) {
              updatedEntityRow.primaryAccount =
                primaryEntityId !== null ? { id: primaryEntityId } : primaryEntityId;
              (updatedEntity as UpdatePersonPayload).primaryAccount =
                primaryEntityId !== null ? { id: primaryEntityId } : primaryEntityId;
            }

            updatedEntities = entities.map((entity) => ({ ...entity, ...updatedEntityRow }));
          }

          return updatedEntity;
        }),
        {
          params: {
            // without this param platform fails the entire request if at least one entity fails
            $process: "any",
          },
        }
      );

      if (response[1] && response[1].length) {
        yield* reloadFunnelsOnLockRefusal(response[1].map(({ errors }) => errors));
        response[1].forEach((error) => {
          switch (entityType) {
            case EntityType.ACTIVITY:
              failedEntityIds.add(error.activityId!);
              break;
            case EntityType.COMPANY:
              failedEntityIds.add(error.accountId!);
              break;
            case EntityType.DEAL:
              failedEntityIds.add(error.dealId!);
              break;
            case EntityType.PERSON:
              failedEntityIds.add(error.contactId!);
              break;
          }
        });

        // removing failed records from updatedEntities
        updatedEntities = updatedEntities.filter(({ id }) => !failedEntityIds.has(id));
        if (intl) {
          // a deal row rejected by its lock carries a readable message of its own
          errors = (isDeal ? response[1] : response[1].slice(0, 1))
            .flatMap((row) => row.errors)
            .map((error) => getErrorMessageByCode(intl, error.code, error) ?? error.message);
        }
      }
    }

    // counted once both paths and any locked deals are settled
    entitiesUpdatedCount = updatedEntities.length;
    entitiesFailedCount = failedEntityIds.size;
    if (intl && lockedDealReasons.size) {
      const lockNotes = Array.from(lockedDealReasons.values(), (reason) =>
        intl.formatMessage(dealLockMessages[reason])
      );
      errors = uniq([...lockNotes, ...errors]);
    } else if (isDeal) {
      errors = uniq(errors);
    }

    if (intl && failedEntityIds.size > 0) {
      const isPartialSuccess = entitiesFailedCount && entitiesUpdatedCount;
      const count = isPartialSuccess ? entitiesUpdatedCount : entitiesFailedCount;

      const description = getRowErrorsDescription(
        errors,
        isDeal,
        !!isPartialSuccess,
        intl.formatMessage(messages.updateWarningDescription, { total: selectedEntities.length })
      );

      (isPartialSuccess ? notification.warn : notification.error)({
        description,
        message: intl.formatMessage(
          isPartialSuccess ? messages.updateWarning : messages.updateFailed,
          { count, entityType: camelCase(entityType) }
        ),
      });
    } else {
      if (intl && updatedEntities.length) {
        notification.success({
          message: intl.formatMessage(messages.successfullyUpdated, {
            count: updatedEntities.length,
            entityType: camelCase(entityType),
          }),
        });
      }
    }

    yield put(updateEntities.success());
    yield put(notifyAboutChanges({ entityType, updated: updatedEntities }));
    yield call(onSuccess);
  } catch (error) {
    yield put(updateEntities.failure());
    yield put(handleError({ error }));
  }
}

export function* onBulkAddEntitiesToGroups({
  payload: { entities, entityType, groupIds, onSuccess },
}: ReturnType<typeof bulkAddEntitiesToGroups.request>) {
  const filteredEntities = entities.filter(({ entity }) => entity === entityType);
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield all(
      groupIds.map((groupId) =>
        callApi(
          "addToGroup",
          orgId,
          groupId,
          entityType,
          filteredEntities.map(({ id }) => id)
        )
      )
    );

    yield put(reloadGroups({ entityType }));

    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        message: intl.formatMessage(messages.successfullyAddedToGroups, {
          count: filteredEntities.length,
          entityName: getEntityTypeDisplayName(intl, entityType, {
            lowercase: false,
            plural: filteredEntities.length > 1,
          }),
        }),
      });
    }

    yield put(bulkAddEntitiesToGroups.success());

    onSuccess();
  } catch (error) {
    yield put(bulkAddEntitiesToGroups.failure());
    yield put(handleError({ error }));
  }
}

export function* onBulkRemoveEntitiesFromGroups({
  payload: { entities, entityType, groupIds, onSuccess },
}: ReturnType<typeof bulkRemoveEntitiesFromGroups.request>) {
  const filteredEntities = entities.filter(({ entity }) => entity === entityType);
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield all(
      groupIds.map((groupId) =>
        callApi(
          "deleteFromGroup",
          orgId,
          groupId,
          entityType,
          filteredEntities.map(({ id }) => id)
        )
      )
    );

    yield put(reloadGroups({ entityType }));

    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        message: intl.formatMessage(messages.successfullyDeletedFromGroups, {
          count: filteredEntities.length,
          entityName: getEntityTypeDisplayName(intl, entityType, {
            lowercase: false,
            plural: filteredEntities.length > 1,
          }),
        }),
      });
    }

    yield put(bulkRemoveEntitiesFromGroups.success());

    onSuccess();
  } catch (error) {
    yield put(bulkRemoveEntitiesFromGroups.failure());
    yield put(handleError({ error }));
  }
}

type RouteStopRef = {
  entityType: EntityTypesSupportingRoutes;
  id: EntitiesSupportingRoutes["id"];
};

type BulkAddResult = {
  addedRefs?: RouteStopRef[];
  message?: string;
  routeName?: string;
  success: boolean;
};

const routeStopRefKey = (ref: RouteStopRef) => `${ref.entityType}:${ref.id}`;

// A company/person stop maps to a ref for its own type; manual stops have no entity ref.
const toRouteStopRef = (stop: RouteStop): RouteStopRef | undefined => {
  if (stop.entity === EntityType.COMPANY) {
    return { id: stop.accountId, entityType: EntityType.COMPANY };
  }
  if (stop.entity === EntityType.PERSON) {
    return { id: stop.contactId, entityType: EntityType.PERSON };
  }
  return undefined;
};

// Company/person pins become stops of their own type; deals (when included) resolve to the chosen
// side via dealAssociatedEntityType. Companies and people are added together — no single type is forced.
function* getRouteStopRefs(
  entities: EntitiesSupportingBulkEdit[],
  dealAssociatedEntityType: EntityTypesSupportingRoutes,
  includeDeals: boolean
): Generator<unknown, RouteStopRef[], any> {
  const orgId: Organization["id"] = yield select(getOrganizationId);
  const refs: RouteStopRef[] = [];
  const seen = new Set<string>();
  const addRef = (entityType: EntityTypesSupportingRoutes, id: EntitiesSupportingRoutes["id"]) => {
    const ref: RouteStopRef = { id, entityType };
    const key = routeStopRefKey(ref);
    if (!seen.has(key)) {
      seen.add(key);
      refs.push(ref);
    }
  };
  const dealIds: Deal["id"][] = [];
  entities.forEach((entity) => {
    if (entity.entity === EntityType.COMPANY) {
      addRef(EntityType.COMPANY, entity.id);
    } else if (entity.entity === EntityType.PERSON) {
      addRef(EntityType.PERSON, entity.id);
    } else if (includeDeals && entity.entity === EntityType.DEAL) {
      dealIds.push(entity.id);
    }
  });
  if (dealIds.length) {
    const dealResponse: ListResponse<Deal> = yield callApi("fetchDeals", orgId, {
      $filters: { $and: [{ id: { $in: dealIds } }] },
    });
    dealResponse.data.forEach((deal) => {
      if (dealAssociatedEntityType === EntityType.COMPANY && deal.account) {
        addRef(EntityType.COMPANY, deal.account.id);
      } else if (dealAssociatedEntityType === EntityType.PERSON && deal.contact) {
        addRef(EntityType.PERSON, deal.contact.id);
      }
    });
  }
  return refs;
}

export function* onBulkAddEntitiesToRoutes({
  payload: { entities, entityType, includeDeals, onSuccess, routeIds },
}: ReturnType<typeof bulkAddEntitiesToRoutes.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const refs: RouteStopRef[] = yield getRouteStopRefs(entities, entityType, includeDeals);

    const results: BulkAddResult[] = yield all(
      routeIds.map(function* (routeId: Route["id"]): Generator<unknown, BulkAddResult, any> {
        const route: Route = yield callApi("fetchUnifiedRoute", orgId, routeId);

        // Keys of stops already on the route, across both stop types.
        const existingKeys = new Set(
          route.stopTime.map(toRouteStopRef).filter(isDefined).map(routeStopRefKey)
        );
        const newRefs = refs.filter((ref) => !existingKeys.has(routeStopRefKey(ref)));

        // Everything already on the route — nothing to send for this route.
        if (newRefs.length === 0) {
          return { addedRefs: [], success: true };
        }

        // Cap the WHOLE route (manual + every entity type), not just same-type stops.
        if (route.stopTime.length + newRefs.length > MAX_ROUTE_STOPS) {
          return {
            message: i18nService.formatMessage(messages.failedTooManyStops),
            routeName: route.name,
            success: false,
          };
        }

        const newStopItems: CreateRouteStopItem[] = newRefs.map((ref) =>
          ref.entityType === EntityType.COMPANY ? { accountId: ref.id } : { contactId: ref.id }
        );
        const basePayload = buildPreviewPayloadFromRoute(route);
        const previewPayload: PreviewRoutePayload = {
          routeDetail: basePayload.routeDetail,
          stopTime: [...basePayload.stopTime, ...newStopItems],
        };

        let metrics: RoutePreviewMetrics | undefined;
        try {
          const preview: RoutePreview = yield callApi("previewUnifiedRoute", orgId, previewPayload);
          metrics = getRoutePreviewMetrics(preview);
        } catch (error) {
          return {
            message: getErrorMessage(
              error as Error,
              routeValidationErrorMessages,
              messages.failedToBuild
            ),
            routeName: route.name,
            success: false,
          };
        }

        const appendOps: RouteStopBulkItem[] = newRefs.map((ref) => ({
          _action_: "c" as const,
          ...(ref.entityType === EntityType.COMPANY
            ? { accountId: ref.id }
            : { contactId: ref.id }),
        }));
        yield callApi("bulkUpdateUnifiedRouteStops", orgId, routeId, appendOps, metrics);

        return { addedRefs: newRefs, success: true };
      })
    );

    const failedResults = results.filter(({ success }) => !success);

    // Distinct records actually added across the selected routes — the count the toast reports.
    const addedRecords = uniqBy(
      results.flatMap((result) => result.addedRefs ?? []),
      routeStopRefKey
    );

    const intl = i18nService.getIntl();
    if (intl) {
      if (addedRecords.length > 0) {
        notification.success({
          message: intl.formatMessage(messages.successfullyAddedToRoutes, {
            count: addedRecords.length,
            entityName: intl.formatMessage(messages.recordEntityName, {
              count: addedRecords.length,
            }),
            routeCount: results.length - failedResults.length,
          }),
        });
      } else if (failedResults.length === 0) {
        notification.info({ message: intl.formatMessage(messages.noRecordsToAdd) });
      }

      failedResults.forEach(({ message, routeName }) =>
        notification.error({
          description: message,
          message: i18nService.formatMessage(messages.failedTitle, "", {
            routeName: routeName!,
          }),
        })
      );
    }

    yield put(notifyAboutChanges({ entityType: EntityType.ROUTE, updatedIds: routeIds }));
    yield put(bulkAddEntitiesToRoutes.success());

    onSuccess();
  } catch (error) {
    yield put(bulkAddEntitiesToRoutes.failure());
    yield put(handleError({ error }));
  }
}

export function* onBulkRemoveEntitiesFromRoutes({
  payload: { entities, entityType, includeDeals, onSuccess, routeIds },
}: ReturnType<typeof bulkRemoveEntitiesFromRoutes.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const refs: RouteStopRef[] = yield getRouteStopRefs(entities, entityType, includeDeals);
    const refKeys = new Set(refs.map(routeStopRefKey));

    const removeResults: Array<{ removedRefs: RouteStopRef[] }> = yield all(
      routeIds.map(function* (
        routeId: Route["id"]
      ): Generator<unknown, { removedRefs: RouteStopRef[] }, any> {
        const route: Route = yield callApi("fetchUnifiedRoute", orgId, routeId);
        const removeStops = route.stopTime.filter((stop) => {
          const ref = toRouteStopRef(stop);
          return ref ? refKeys.has(routeStopRefKey(ref)) : false;
        });
        if (removeStops.length === 0) {
          return { removedRefs: [] };
        }
        const removeOps: RouteStopBulkItem[] = removeStops.map((stop) => ({
          id: stop.id,
          _action_: "d" as const,
        }));

        const removedIds = new Set(removeStops.map((stop) => stop.id));
        const survivors = route.stopTime.filter((stop) => !removedIds.has(stop.id));
        const survivorsComplete = isRouteComplete(survivors);
        let metrics: RoutePreviewMetrics | undefined;
        // A delete is never geometry-neutral, so with no envelope the BE nulls every metric. When
        // the survivors leave the route complete, price the whole drive as a Re-build does.
        if (survivors.length > 0) {
          try {
            const preview: RoutePreview = yield callApi(
              "previewUnifiedRoute",
              orgId,
              buildPreviewPayloadFromRoute(
                { routeDetail: route.routeDetail, stopTime: survivors },
                { allStops: survivorsComplete }
              )
            );
            metrics = getRoutePreviewMetrics(preview);
          } catch (error) {
            // Best-effort: preview failure must not block the removal — log, no toast.
            yield put(handleError({ displayNotification: false, error }));
          }
        }

        yield callApi("bulkUpdateUnifiedRouteStops", orgId, routeId, removeOps, metrics);

        return { removedRefs: removeStops.map(toRouteStopRef).filter(isDefined) };
      })
    );

    // Distinct records actually removed across the selected routes — the count the toast reports.
    const removedRecords = uniqBy(
      removeResults.flatMap((result) => result.removedRefs),
      routeStopRefKey
    );

    const intl = i18nService.getIntl();
    if (intl) {
      if (removedRecords.length > 0) {
        notification.success({
          message: intl.formatMessage(messages.successfullyDeletedFromRoutes, {
            count: removedRecords.length,
            entityName: intl.formatMessage(messages.recordEntityName, {
              count: removedRecords.length,
            }),
            routeCount: routeIds.length,
          }),
        });
      } else {
        notification.info({ message: intl.formatMessage(messages.noRecordsToRemove) });
      }
    }

    yield put(notifyAboutChanges({ entityType: EntityType.ROUTE, updatedIds: routeIds }));
    yield put(bulkRemoveEntitiesFromRoutes.success());

    onSuccess();
  } catch (error) {
    yield put(bulkRemoveEntitiesFromRoutes.failure());
    yield put(handleError({ error }));
  }
}

export function* onInitializeRouteModal({
  payload: { callback, entities, entityType, includeDeals, newRoute },
}: ReturnType<typeof initializeRouteModal.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const refs: RouteStopRef[] = yield getRouteStopRefs(entities, entityType, includeDeals);

    const companyIds = refs
      .filter((ref) => ref.entityType === EntityType.COMPANY)
      .map((ref) => ref.id);
    const personIds = refs
      .filter((ref) => ref.entityType === EntityType.PERSON)
      .map((ref) => ref.id);

    const allRoutesResponse: ListResponse<Route> = yield callApi("fetchUnifiedRoutes", orgId, {
      $filters: { includeAccessStatus: true },
      $limit: 1000,
      $order: ["name"],
    });

    // Routes that already contain any of the selected companies/people — drives the route list
    // filter for the Remove operation. One query, OR-combining the present types.
    const existingRouteConditions: Array<
      | { accounts: { $in: EntitiesSupportingRoutes["id"][] } }
      | { contacts: { $in: EntitiesSupportingRoutes["id"][] } }
    > = [];

    if (companyIds.length) {
      existingRouteConditions.push({ accounts: { $in: companyIds } });
    }
    if (personIds.length) {
      existingRouteConditions.push({ contacts: { $in: personIds } });
    }

    let existingRoutesIds: Route["id"][] = [];
    if (existingRouteConditions.length) {
      const existingRoutesResponse: ListResponse<Route> = yield callApi(
        "fetchUnifiedRoutes",
        orgId,
        { $filters: { $or: existingRouteConditions } }
      );
      existingRoutesIds = existingRoutesResponse.data.map(({ id }) => id);
    }

    if (callback) {
      yield call(callback);
    }

    const routes = allRoutesResponse.data.filter(({ accessStatus }) => accessStatus.update);
    if (newRoute && !routes.some(({ id }) => id === newRoute.id)) {
      routes.push(newRoute);
    }

    yield put(
      initializeRouteModal.success({
        existingRoutesIds,
        routes,
        totalRoutes: allRoutesResponse.accessible,
      })
    );
  } catch (error) {
    yield put(initializeRouteModal.failure());
    yield put(handleError({ error }));
  }
}

export function* onFilterRoutes({ payload }: ReturnType<typeof filterRoutes.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const routes: ListResponse<Route> = yield callApi("fetchUnifiedRoutes", orgId, {
      $filters: {
        includeAccessStatus: true,
        name: payload.query ? { $in: payload.query } : undefined,
      },
      $limit: 100,
      $order: ["name"],
    });
    yield put(filterRoutes.success(routes.data));
  } catch (error) {
    yield put(filterRoutes.failure());
    yield put(handleError({ error }));
  }
}

export function* selectBarSaga() {
  yield takeLatest(deleteEntities.request, onDeleteEntities);
  yield takeLatest(bulkAddEntitiesToGroups.request, onBulkAddEntitiesToGroups);
  yield takeLatest(bulkRemoveEntitiesFromGroups.request, onBulkRemoveEntitiesFromGroups);
  yield takeLatest(bulkAddEntitiesToRoutes.request, onBulkAddEntitiesToRoutes);
  yield takeLatest(bulkRemoveEntitiesFromRoutes.request, onBulkRemoveEntitiesFromRoutes);
  yield takeLatest(updateEntities.request, onUpdateEntities);
  yield takeLatest(initializeRouteModal.request, onInitializeRouteModal);
  yield takeLatest(filterRoutes.request, onFilterRoutes);
}
